<script setup lang="ts">
import {useQuasar} from "quasar";
import {type RouteLocationRaw, useRouter} from "vue-router";
import {useI18n} from "vue-i18n";
import {useDashboardStore, useLocalizationStore} from "@/store";
import {computed, type ComputedRef, nextTick, onMounted, onUnmounted, ref} from "vue";
import {type EocSummaryDetails, getEocSummaryDetails} from "@/api/endpoints/statisticsWeb.ts";
import {EpisodeOfCareSummaryType} from "@/constants/enums.ts";
import {handleApiRequest} from "@/api/handleApiRequest.ts";
import {deliveryD8Str} from "@/pages/RecoveryUpdates/utils/rcvyUpdtsUtils.ts";
import Onboarding from "@/pages/RecoveryUpdates/components/Onboarding.vue";
import Graphing from "@/pages/RecoveryUpdates/components/Graphing.vue";

const props = defineProps({
  eocId: {
    type: Number,
    required: true,
  },
  sumId: {
    type: Number,
    required: true,
  }
});

const $q = useQuasar(),
  router = useRouter(),
  {t} = useI18n();

const dashboardStore = useDashboardStore(),
  {locale} = useLocalizationStore();

const details = ref<EocSummaryDetails | undefined>(undefined),
  graphing = ref<InstanceType<typeof Graphing>[]>([]),
  isLdg = ref<boolean>(false);

const hasAiSums: ComputedRef<boolean> = computed(() => dashboardStore.patientConfig?.features.isAIPatientSummariesEnabled ?? false),
  hasGraph: ComputedRef<boolean> = computed(() => !isOnbdType.value && !!details.value?.charts?.length),
  isOnbdType: ComputedRef<boolean> = computed(() => details.value?.summaryType === EpisodeOfCareSummaryType.Onboarding),
  noticeKey: ComputedRef<string> = computed(() => isOnbdType.value ? 'summaries_onboarding_ai_updates_notice' : 'message_ai_generated');

function onBackClick() {
  let routeObj: RouteLocationRaw;
  
  if (history.state.from === 'RecoveryUpdates.List') {
    routeObj = { name: 'RecoveryUpdates.List' };
  } else {
    routeObj = { name: 'Dashboard.Home', params: { reload: 'true' } };
  }
  
  router.push(routeObj);
}

onMounted(async () => {
  isLdg.value = true;
  
  if (props.eocId && props.sumId && hasAiSums.value) {
    const eocSumDetResp = await handleApiRequest(() => getEocSummaryDetails(props.eocId, props.sumId), null, $q);
    
    if (eocSumDetResp) details.value = eocSumDetResp.result;
  } else {
    router.replace({ name: 'Dashboard.Home', params: { reload: 'true' } });
  }
  
  isLdg.value = false;

  await nextTick();

  if (hasGraph.value) await Promise.all(graphing.value.map(g => g.init()));
});

onUnmounted(() => {
  details.value = undefined;
  graphing.value = [];
  isLdg.value = false;
})
</script>

<template>
  <q-page padding>
    <zbm-multi-view-layout :box-padding="$zb.enums.BoxPaddingEnum.p30"
                           :loading="isLdg"
                           :onBackClick="onBackClick">

      <!-- Heading -->
      <template #heading>{{ t('patient_recovery_update') }}</template>

      <!-- View Unique Content -->
      <template #default>
        <zbm-skeleton-loader height="600px"
                             :loading="isLdg"
                             width="100%">
        
          <!-- Delivery Date -->
          <p v-if="details?.deliveryDate"
             class="delivery-d8">
            {{ deliveryD8Str(details?.deliveryDate, locale, t) }}
          </p>
          
          <!-- Title -->
          <h3 v-if="details?.title">{{ details?.title }}</h3>
          
          <!-- Details -->
          <p v-if="details?.details"
             v-html="details.details"
             :class="[isOnbdType ? 'mb-40' : 'mb-24']" />
          
          <!-- Onboarding -->
          <Onboarding v-if="isOnbdType" />

          <!-- Notice (not Onboarding -->
          <p class="notice"><img alt="" src="/img/rcvy-updts-onbd-sparkles.svg" />{{ t(noticeKey) }}</p>
          
          <!-- Graphing -->
          <template v-if="hasGraph">
            <Graphing v-for="(chart, i) in details?.charts"
                      :chart="chart"
                      :key="i"
                      ref="graphing" />
          </template>
        </zbm-skeleton-loader>
      </template>
    </zbm-multi-view-layout>
  </q-page>
</template>

<style scoped lang="scss">
@use 'sass:map';

:deep(.multi-view-layout__content) {
  min-height: 0;
}

// Delivery Date
.delivery-d8 { // p
  font-size: 14px;
  line-height: 18px;
  color: map.get($co-brand-clrs, content-txt-ttl);
  margin-block-end: 4px;
}

// Title
h3 {
  font-weight: map.get($text-weights, semibold);
  line-height: 28px;
  margin-block-end: 24px;
}

// Details
.details { // p
  font-size: 18px;
  line-height: 140%;
  margin-block-end: 40px;
}

// Notice
.notice { // p
  font-size: 12px;
  line-height: normal;
  color: map.get($co-brand-clrs, content-txt-subtitle);

  &:not(:last-child) {
    margin-block-end: 20px;
  }

  img {
    $size: 10px;

    width: $size;
    height: $size;
    margin-inline-end: 6px;
  }
}
</style>